import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import {
  Home, TrendingUp, Receipt, Loader2, FileDown,
  Share2, MessageCircle, Mail,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useSeedBackTo } from "@/hooks/useSeedBackTo";
import { useShareToAccountant } from "@/hooks/useShareToAccountant";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { SUB_LABEL, fmt } from "@/components/personal-tax/constants";
import RentalsTab from "@/components/personal-tax/RentalsTab";
import InvestmentsTab from "@/components/personal-tax/InvestmentsTab";
import DeductionsTab from "@/components/personal-tax/DeductionsTab";
import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import RulesetStamp from "@/components/tax/RulesetStamp";
import FlagsPreflightCard from "@/components/tax/FlagsPreflightCard";
import PdfPreviewSheet from "@/components/tax/PdfPreviewSheet";
import BiSelect from "@/components/BiSelect";

/**
 * Personal Tax MVP — three tabs:
 *   Rentals      — properties + income/expense per property
 *   Investments  — dividends, franking credits, interest, distributions
 *   Deductions   — WFH hours, tools <$300, self-ed, uniform, insurance, etc.
 *
 * All entries persist to db.personal_entries via /api/personal-tax/entries.
 * The end-of-year PDF + WhatsApp/Email/Share row lives at the bottom (reuses
 * useShareToAccountant hook for parity with BAS/TPAR/Pack).
 *
 * Tabs are extracted into /components/personal-tax/{Rentals,Investments,Deductions}Tab.jsx
 * to keep this page under the 700-line file guideline.
 */
export default function PersonalTaxPage() {
  const { profile } = useApp();
  const { user } = useAuth();
  const { accountantName, accountantEmail, shareVia: shareToAccountant, prefetchPdf } =
    useShareToAccountant();

  const currentFy = useMemo(() => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  }, []);
  const [fy, setFy] = useState(currentFy);
  const [tab, setTab] = useState("rentals");
  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlBusy, setDlBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCfg, setPreviewCfg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: p }, { data: e }] = await Promise.all([
        api.get(`/personal-tax/summary?fy=${fy}`),
        api.get(`/personal-tax/properties`),
        api.get(`/personal-tax/entries?fy=${fy}`),
      ]);
      setSummary(s);
      setProperties(p);
      setEntries(e);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Could not load personal tax data");
      setSummary(null);
      setProperties([]);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  // Warm the Personal Tax PDF cache so Share triggers the native OS
  // share sheet synchronously (iOS Safari requirement).
  useEffect(() => {
    prefetchPdf(
      `/export/personal-tax-pdf?fy=${fy}&with_evidence=1`,
      `buildintel-personal-tax-FY${String(fy).slice(-2)}.pdf`
    );
  }, [fy, prefetchPdf]);

  // First tap opens the in-app preview sheet. Share happens from inside.
  // Declared AFTER buildPdfSummary + displayName (see below) so both
  // references are already initialised by the time this useCallback closes
  // over them. Placeholder here is filled in below.
  // (openPreview implementation moved below buildPdfSummary)

  // Sanitise the display name used in outward text summaries (WhatsApp/Email
  // body + subject). Mirrors the backend `pdf_utils._sanitise_name` so the
  // PDF header, the email subject, and the message body never contradict
  // each other by putting "Admin" on one and the real name on another.
  const cleanName = (v) => {
    const s = (v || "").trim();
    if (!s) return "";
    if (["admin", "administrator", "test", "user"].includes(s.toLowerCase())) return "";
    return s;
  };
  const displayName =
    cleanName(profile?.individual_name) ||
    cleanName(user?.name) ||
    (user?.email || "");
  const hasCustomName = Boolean(cleanName(profile?.individual_name) || cleanName(user?.name));

  const buildPdfSummary = useCallback(() => {
    if (!summary) return "";
    const bizName = profile?.business_name || "";
    const abn = profile?.abn || "";
    const personName = displayName;
    const greeting = accountantName ? `Hi ${accountantName},\n\n` : "";
    // Lead with the INDIVIDUAL — this is a personal document, not a business
    // pack (the header on the PDF itself uses draw_individual_header, so
    // both the body text and the attachment now agree on who owns it).
    const personLine = personName ? `${personName}\n` : "";
    const linkedBiz = bizName
      ? `Linked business: ${bizName}${abn ? ` · ABN ${abn}` : ""}\n`
      : "";
    const line = (l, v) =>
      `${l}: $${Number(v || 0).toLocaleString("en-AU", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}`;
    const dedn = Object.entries(summary.deductions.by_category || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([k, v]) => `• ${SUB_LABEL[k] || k}: $${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
      .join("\n");
    return (
      `${greeting}${personLine}${linkedBiz}Personal Tax Summary · ${summary.fy_label}\n\n` +
      `Rental properties: ${summary.rental.property_count}\n` +
      line("Net rental income", summary.rental.net) + `\n` +
      `  Gross: $${summary.rental.gross.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `  Expenses: $${summary.rental.expenses.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n` +
      line("Dividends", summary.investments.dividends) + `\n` +
      line("Franking credits", summary.investments.franking_credits) + `\n` +
      line("Grossed-up", summary.investments.grossed_up) + `\n\n` +
      `Personal deductions total: $${summary.deductions.total.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      (dedn ? `${dedn}\n\n` : "\n") +
      `Full Personal Tax Summary PDF attached (prepared with BuildIntel AI). ` +
      `Please review before lodging.`
    );
  }, [summary, profile, accountantName, displayName]);

  // First tap opens the in-app preview sheet. Share happens from inside.
  const openPreview = useCallback(() => {
    if (!summary) {
      toast.error("Personal Tax summary still loading — try again in a moment");
      return;
    }
    const text = buildPdfSummary();
    const personName = displayName || "Personal Tax";
    const subject = `${personName} — Personal Tax ${summary.fy_label}`;
    setPreviewCfg({
      pdfUrl: `/export/personal-tax-pdf?fy=${fy}&with_evidence=1`,
      filename: `${(personName || "buildintel").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-personal-tax-fy${String(fy).slice(-2)}.pdf`,
      title: `Personal Tax · ${summary.fy_label}`,
      description: "Review the pages before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
  }, [summary, buildPdfSummary, displayName, fy]);
  const grabPdf = openPreview;

  // Every share entry point routes through the same in-app preview so
  // the tradie always sees the pages BEFORE any OS share sheet fires.
  // eslint-disable-next-line no-unused-vars
  const sharePersonal = useCallback(async (_kind) => {
    if (!profile || !summary) {
      toast.error("Still loading — try again in a moment");
      return;
    }
    openPreview();
  }, [profile, summary, openPreview]);

  // eslint-disable-next-line no-unused-vars
  const _legacyGrab = async () => {
    setDlBusy(true);
    try {
      await downloadFile(
        `/api/export/personal-tax-pdf?fy=${fy}`,
        `buildintel-personal-tax-FY${String(fy).slice(-2)}.pdf`
      );
      toast.success(`Personal Tax PDF FY${String(fy).slice(-2)} downloaded`);
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDlBusy(false);
    }
  };

  return (
    <div className="space-y-4 pb-24" data-testid="personal-tax-page">
      <PageHeader
        icon={Receipt}
        title="Personal Tax"
        subtitle={`Rentals · Investments · Deductions · ${summary?.fy_label || `FY${String(fy).slice(-2)}`}`}
        testid="pt-header"
        backTo="/tax"
      />

      {/* Nudge tradies to add their real name so the Personal Tax PDF doesn't
          fall back to "Admin" / their email in the primary header. Only shows
          when neither `profile.individual_name` nor a real `user.name` is set. */}
      {!hasCustomName && (
        <div
          data-testid="pt-name-missing-nudge"
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-[12px] text-amber-100/90 flex items-center gap-2"
        >
          <span className="flex-1">
            <b className="text-amber-200">Add your name for tax packs</b> —
            your Personal Tax PDF is currently using a fallback label.
          </span>
          <Link
            to="/settings"
            data-testid="pt-name-missing-cta"
            className="shrink-0 px-2.5 h-8 inline-flex items-center rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-[11px] font-heading font-bold uppercase tracking-wider"
          >
            Open Settings
          </Link>
        </div>
      )}

      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">FY</label>
        <BiSelect
          data-testid="pt-fy-picker"
          className="bi-input h-9 py-0 text-sm"
          value={fy}
          onChange={(e) => setFy(Number(e.target.value))}
        >
          {[currentFy + 1, currentFy, currentFy - 1, currentFy - 2].map((y) => (
            <option key={y} value={y}>{`FY${String(y).slice(-2)}`}</option>
          ))}
        </BiSelect>
        <button
          onClick={load}
          data-testid="pt-refresh"
          className="ml-auto text-[11px] text-white/50 hover:text-white/80"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-2" data-testid="pt-kpis">
          <KpiCard
            label="Rental net"
            value={fmt(summary.rental.net)}
            hint={`${summary.rental.property_count} propert${summary.rental.property_count === 1 ? "y" : "ies"}`}
            testid="kpi-rental"
          />
          <KpiCard
            label="Investments"
            value={fmt(summary.investments.grossed_up)}
            hint={`Div ${fmt(summary.investments.dividends)} · FC ${fmt(summary.investments.franking_credits)}`}
            testid="kpi-investments"
          />
          <KpiCard
            label="Deductions"
            value={fmt(summary.deductions.total)}
            hint={`${Object.keys(summary.deductions.by_category || {}).length} categories`}
            testid="kpi-deductions"
          />
        </div>
      )}

      <AtoDisclaimer context="personal_tax" compact testId="pt-disclaimer" />

      <div className="grid grid-cols-3 gap-1 rounded-xl bg-white/5 p-1" data-testid="pt-tabs">
        <TabBtn active={tab === "rentals"} onClick={() => setTab("rentals")} testid="tab-rentals">
          <Home className="w-3.5 h-3.5" /> Rentals
        </TabBtn>
        <TabBtn active={tab === "investments"} onClick={() => setTab("investments")} testid="tab-investments">
          <TrendingUp className="w-3.5 h-3.5" /> Investments
        </TabBtn>
        <TabBtn active={tab === "deductions"} onClick={() => setTab("deductions")} testid="tab-deductions">
          <Receipt className="w-3.5 h-3.5" /> Deductions
        </TabBtn>
      </div>

      {tab === "rentals" && (
        <RentalsTab
          properties={properties}
          entries={entries.filter((e) => e.type === "rental_income" || e.type === "rental_expense")}
          onChange={load}
        />
      )}
      {tab === "investments" && (
        <InvestmentsTab
          entries={entries.filter((e) => e.type === "dividend" || e.type === "franking_credit")}
          onChange={load}
        />
      )}
      {tab === "deductions" && (
        <DeductionsTab
          entries={entries.filter((e) => e.type === "deduction")}
          onChange={load}
        />
      )}

      <div className="sticky bottom-20 sm:bottom-24 pt-1 z-20 space-y-2">
        {/* Pack-intelligence preflight — Personal Tax only cares about
            missing receipts on personal deductions. Advisory only. */}
        <FlagsPreflightCard
          fy={fy}
          scope={["no_receipt"]}
          testid="pt-flags-preflight"
        />
        <button
          data-testid="pt-download-pdf"
          onClick={grabPdf}
          disabled={dlBusy}
          className="w-full h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)] backdrop-blur"
        >
          {dlBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileDown className="w-5 h-5" />}
          {dlBusy ? "Building your PDF…" : `Download Personal Tax PDF (${summary?.fy_label || `FY${String(fy).slice(-2)}`})`}
        </button>

        <div className="bi-surface p-3 backdrop-blur" data-testid="pt-share-row">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-2">
            <Share2 className="w-3 h-3" /> Send to {accountantName ? accountantName : "accountant"}
            {accountantEmail && (
              <span className="text-white/30 normal-case tracking-normal truncate">· {accountantEmail}</span>
            )}
            {!profile && (
              <span className="ml-auto text-white/30 normal-case tracking-normal inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> loading profile…
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <ShareBtn testid="pt-share-whatsapp" onClick={() => sharePersonal("whatsapp")} disabled={shareBusy || !profile}
              icon={<MessageCircle className="w-4 h-4 text-[#25D366]" />}>WhatsApp</ShareBtn>
            <ShareBtn testid="pt-share-email" onClick={() => sharePersonal("email")} disabled={shareBusy || !profile}
              icon={<Mail className="w-4 h-4 text-[#60A5FA]" />}>Email</ShareBtn>
            <ShareBtn testid="pt-share-native" onClick={() => sharePersonal("native")} disabled={shareBusy || !profile}
              icon={shareBusy ? <Loader2 className="w-4 h-4 animate-spin text-[#FF9500]" /> : <Share2 className="w-4 h-4 text-[#FF9500]" />}>Share PDF</ShareBtn>
          </div>
        </div>
        <div className="text-center pt-1" data-testid="pt-ruleset-footer">
          <RulesetStamp />
        </div>
      </div>

      <PdfPreviewSheet
        open={previewOpen}
        onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreviewCfg(null); }}
        pdfUrl={previewCfg?.pdfUrl}
        filename={previewCfg?.filename}
        title={previewCfg?.title}
        description={previewCfg?.description}
        shareText={previewCfg?.shareText}
        shareSubject={previewCfg?.shareSubject}
        testid="personal-tax-preview"
      />
    </div>
  );
}

function KpiCard({ label, value, hint, testid }) {
  return (
    <div className="bi-surface p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-heading truncate">{label}</div>
      <div className="font-heading text-[15px] sm:text-base font-extrabold text-white truncate">{value}</div>
      {hint && <div className="text-[9px] text-white/40 truncate mt-0.5">{hint}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`h-10 rounded-lg text-[11px] font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition ${
        active
          ? "bg-[#FF9500] text-white"
          : "text-white/50 hover:text-white/90"
      }`}
    >
      {children}
    </button>
  );
}

function ShareBtn({ testid, onClick, disabled, icon, children }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition disabled:opacity-60"
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
